/** BRAND AUTO — trois photos, une identité.
 *  Le pro n'a pas de charte graphique : il a des photos. On en tire sa palette et sa typo. */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, ImagePlus, Loader2, Sparkles, Trash2, X } from "lucide-react";
import { api, messageErreur } from "../api";
import { Btn, EnTete, Micro } from "../components/Bits";
import { chargerPolices } from "../lib/themes";

function Vignette({ photo, surRetrait, index }) {
  return (
    <div
      className="relative overflow-hidden"
      style={{ borderRadius: "var(--radius-md)", aspectRatio: "1 / 1" }}
    >
      <img src={photo.apercu} alt="" className="h-full w-full object-cover" />
      <button
        onClick={surRetrait}
        data-testid={`marque-retirer-${index}`}
        className="absolute top-1.5 right-1.5 inline-flex h-7 w-7 items-center justify-center"
        style={{ borderRadius: 999, background: "rgba(20,20,19,.62)", color: "#fff" }}
      >
        <X size={14} />
      </button>
    </div>
  );
}

function Apercu({ p }) {
  return (
    <div
      className="p-4"
      style={{ background: p.doux, borderRadius: "var(--radius-md)" }}
    >
      <p
        className="text-[11px] tracking-[.14em] uppercase"
        style={{ color: p.accent, fontFamily: `"${p.police_texte}", sans-serif` }}
      >
        Réserver
      </p>
      <p
        className="text-[22px] leading-tight mt-1"
        style={{ color: p.accent, fontFamily: `"${p.police_titre}", serif` }}
      >
        Prends ta place
      </p>
      <p
        className="text-[13px] mt-1.5"
        style={{ color: "var(--text-muted)", fontFamily: `"${p.police_texte}", sans-serif` }}
      >
        Pose gel · 1 h 15 · 55 €
      </p>
      <span
        className="inline-flex items-center h-9 px-4 mt-3 text-[13px] font-semibold"
        style={{
          background: p.accent,
          color: "#fff",
          borderRadius: 999,
          fontFamily: `"${p.police_texte}", sans-serif`,
        }}
      >
        Choisir ce créneau
      </span>
    </div>
  );
}

function Piste({ p, choisie, surChoix, index }) {
  return (
    <button
      onClick={surChoix}
      data-testid={`marque-piste-${index}`}
      className="w-full text-left p-4"
      style={{
        background: "var(--surface)",
        border: `1px solid ${choisie ? p.accent : "var(--border)"}`,
        borderRadius: "var(--radius-lg)",
        boxShadow: choisie ? `0 0 0 1px ${p.accent}` : "none",
        transition: "border-color .2s ease, box-shadow .2s ease",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Micro>{p.angle}</Micro>
          <p className="text-[19px] font-semibold mt-0.5" style={{ color: "var(--text)" }}>
            {p.nom}
          </p>
        </div>
        <span
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center"
          style={{
            borderRadius: 8,
            background: choisie ? p.accent : "transparent",
            border: `1px solid ${choisie ? p.accent : "var(--border)"}`,
            color: "#fff",
          }}
        >
          {choisie && <Check size={14} strokeWidth={3} />}
        </span>
      </div>

      <div className="flex gap-1.5 mt-3">
        {[p.accent, p.voile, p.doux].map((c) => (
          <span
            key={c}
            className="flex-1 h-9"
            style={{ background: c, borderRadius: 8, border: "1px solid rgba(20,20,19,.06)" }}
          />
        ))}
      </div>
      <p className="text-[12px] num mt-1.5" style={{ color: "var(--text-subtle)" }}>
        {p.accent} · {p.voile} · {p.doux}
      </p>

      <p className="text-[13px] mt-3" style={{ color: "var(--text-muted)" }}>
        <span className="font-semibold" style={{ color: "var(--text)" }}>
          {p.police_nom}
        </span>{" "}
        — {p.police_note}
      </p>

      <div className="mt-3">
        <Apercu p={p} />
      </div>
    </button>
  );
}

export default function Marque() {
  const [photos, setPhotos] = useState([]);
  const [pistes, setPistes] = useState([]);
  const [choix, setChoix] = useState(0);
  const [occupe, setOccupe] = useState(false);
  const [retrait, setRetrait] = useState(false);
  const [actuelle, setActuelle] = useState(null);
  const champ = useRef(null);

  useEffect(() => {
    api
      .get("/marque")
      .then(({ data }) => {
        setActuelle(data.theme === "perso" ? data.marque : null);
        if (data.marque) chargerPolices(data.marque);
      })
      .catch(() => {});
  }, []);

  const ajouter = (e) => {
    const fichiers = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    setPhotos((v) => [
      ...v,
      ...fichiers.map((f) => ({ fichier: f, apercu: URL.createObjectURL(f) })),
    ]);
    e.target.value = "";
  };

  const analyser = useCallback(async () => {
    if (!photos.length) return;
    setOccupe(true);
    try {
      const fd = new FormData();
      photos.forEach((p) => fd.append("fichiers", p.fichier, p.fichier.name));
      const { data } = await api.post("/marque/analyser", fd, { timeout: 120000 });
      (data.propositions || []).forEach((p) => chargerPolices(p));
      setPistes(data.propositions || []);
      setChoix(0);
    } catch (e) {
      toast.error(messageErreur(e, "Je n'ai pas réussi à lire ces photos."));
    } finally {
      setOccupe(false);
    }
  }, [photos]);

  const appliquer = async () => {
    const p = pistes[choix];
    setOccupe(true);
    try {
      const { data } = await api.put("/marque", {
        accent: p.accent,
        doux: p.doux,
        voile: p.voile,
        police: p.police,
        nom: p.nom,
      });
      setActuelle(data.marque);
      setPistes([]);
      setPhotos([]);
      toast.success("Ta marque habille ta page, l'espace de tes clientes et tes stories.");
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setOccupe(false);
    }
  };

  const retirer = async () => {
    setRetrait(true);
    try {
      await api.delete("/marque");
      setActuelle(null);
      setPistes([]);
      toast.success("Retour aux thèmes prêts à l'emploi.");
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setRetrait(false);
    }
  };

  return (
    <div className="pb-8" data-testid="page-marque">
      <EnTete
        micro="Brand Auto"
        titre="Ta marque, tirée de tes photos"
        sous="Trois photos de ton studio ou de ton travail. J'en sors ta palette et ta typo, tu choisis."
      />

      {actuelle && (
        <div
          className="mt-6 p-4 max-w-[640px]"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-lg)",
          }}
          data-testid="marque-actuelle"
        >
          <Micro>Ta marque actuelle</Micro>
          <div className="flex items-center gap-3 mt-2">
            <div className="flex gap-1">
              {[actuelle.accent, actuelle.voile, actuelle.doux].map((c) => (
                <span
                  key={c}
                  style={{ height: 30, width: 30, borderRadius: 8, background: c }}
                />
              ))}
            </div>
            <div className="min-w-0">
              <p
                className="text-[17px] font-semibold"
                style={{ fontFamily: `"${actuelle.police_titre}", serif` }}
              >
                {actuelle.nom}
              </p>
              <p className="text-[12.5px]" style={{ color: "var(--text-subtle)" }}>
                {actuelle.police_titre} + {actuelle.police_texte}
              </p>
            </div>
          </div>
          <Btn variante="nu" icone={Trash2} className="mt-3" onClick={retirer} disabled={retrait}
            data-testid="marque-retirer-actuelle">
            Revenir aux thèmes prêts
          </Btn>
        </div>
      )}

      <div className="mt-6 max-w-[640px]">
        <Micro className="mb-2.5 block">
          {photos.length ? `${photos.length} photo${photos.length > 1 ? "s" : ""} sur 3` : "Tes photos"}
        </Micro>
        <div className="grid grid-cols-3 gap-2">
          {photos.map((p, i) => (
            <Vignette key={i} photo={p} index={i}
              surRetrait={() => setPhotos((v) => v.filter((_, j) => j !== i))} />
          ))}
          {photos.length < 3 && (
            <button
              onClick={() => champ.current?.click()}
              data-testid="marque-ajouter"
              className="flex flex-col items-center justify-center gap-1.5"
              style={{
                aspectRatio: "1 / 1",
                borderRadius: "var(--radius-md)",
                background: "var(--surface-sunken)",
                border: "1px dashed var(--border-strong, var(--border))",
                color: "var(--text-subtle)",
              }}
            >
              <ImagePlus size={20} strokeWidth={1.6} />
              <span className="text-[12.5px]">Ajouter</span>
            </button>
          )}
        </div>
        <input
          ref={champ}
          type="file"
          accept="image/*"
          multiple
          onChange={ajouter}
          className="hidden"
          data-testid="marque-champ-fichier"
        />

        <p className="text-[12.5px] mt-2.5" style={{ color: "var(--text-subtle)" }}>
          Ce qui marche : une pose colorée, ton plan de travail, un mur ou un textile de ton
          studio. Ce qui ne marche pas : une photo grise ou surexposée.
        </p>

        <Btn
          className="mt-4"
          icone={occupe ? Loader2 : Sparkles}
          onClick={analyser}
          disabled={occupe || !photos.length}
          data-testid="marque-analyser"
        >
          {occupe ? "J'analyse…" : "Générer ma palette"}
        </Btn>
      </div>

      {pistes.length > 0 && (
        <div className="mt-8 max-w-[640px]" data-testid="marque-pistes">
          <Micro className="mb-2.5 block">Trois pistes, tu en gardes une</Micro>
          <div className="space-y-3">
            {pistes.map((p, i) => (
              <Piste key={i} p={p} index={i} choisie={choix === i} surChoix={() => setChoix(i)} />
            ))}
          </div>
          <Btn className="mt-4" icone={Check} onClick={appliquer} disabled={occupe}
            data-testid="marque-appliquer">
            Appliquer « {pistes[choix]?.nom} »
          </Btn>
        </div>
      )}
    </div>
  );
}
